import { Functionality, Pointer } from '@yleisradio/areena-types';
import React, { useMemo } from 'react';
import type {
  AriaAttributes,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  TouchEventHandler,
  WAIAriaRole,
} from 'preact';
import getRoutePath from './getRoutePath';
import { NavLinkWithHistory as NavLink } from './NavLinkWithHistory';
import { To } from 'react-router-dom';
import { logger } from '../../common/logger';
import { useErrorContext } from '../error/ErrorBoundaryContext';
import { DataAttributes, ReactRouterLocationState } from '../appTypings';
import { NOT_FOUND_PATH } from './routeNames';

type NavLinkRenderProps = {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
};

type Props = {
  functionality?: Functionality | undefined;
  pointer?: Pointer | undefined;
  id: string;
  ref?: React.Ref<HTMLAnchorElement>;
  children?: React.ReactNode | ((props: NavLinkRenderProps) => React.ReactNode);
  isGeoBlocked?: boolean;
  className?:
    | string
    | ((props: NavLinkRenderProps) => string | undefined)
    | undefined;
  role?: WAIAriaRole;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  onClickCapture?: MouseEventHandler<HTMLAnchorElement>;
  to?: To;
  replace?: boolean;
  impressionAttributes?: DataAttributes;
  dataEngagementAttributes?: DataAttributes;
  dataTestId?: string;
  state?: ReactRouterLocationState;
  onMouseUp?: MouseEventHandler<HTMLAnchorElement>;
  onMouseDown?: MouseEventHandler<HTMLAnchorElement>;
  onMouseEnter?: MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: MouseEventHandler<HTMLAnchorElement>;
  onKeyDown?: KeyboardEventHandler<HTMLAnchorElement>;
  onKeyUp?: KeyboardEventHandler<HTMLAnchorElement>;
  onTouchStart?: TouchEventHandler<HTMLAnchorElement>;
  onTouchEnd?: TouchEventHandler<HTMLAnchorElement>;
  onFocus?: FocusEventHandler<HTMLAnchorElement>;
  onBlur?: FocusEventHandler<HTMLAnchorElement>;
} & AriaAttributes;

const ReactRouterLink = ({
  functionality,
  pointer,
  id,
  children,
  className,
  role,
  onClick,
  isGeoBlocked,
  to: toOverride,
  state,
  replace,
  impressionAttributes,
  dataEngagementAttributes,
  dataTestId,
  ...props
}: Props) => {
  const isNavigable = toOverride || pointer || functionality;

  if (!isNavigable) {
    logger.debug(
      'ReactRouterLink is missing all routable parameters: "to", "pointer" and "functionality"'
    );
  }

  const { notifyErrorFromComponent } = useErrorContext();

  const to: string | To = useMemo(() => {
    try {
      return toOverride ?? getRoutePath({ pointer, functionality });
    } catch (e) {
      notifyErrorFromComponent(e instanceof Error ? e : new Error(String(e)));
      return `/${NOT_FOUND_PATH}`;
    }
  }, [functionality, notifyErrorFromComponent, pointer, toOverride]);

  return (
    <NavLink
      to={to}
      className={className}
      id={id}
      state={state || { areenaApiUri: pointer?.uri }}
      onClick={onClick}
      onMouseUp={props.onMouseUp}
      onMouseDown={props.onMouseDown}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onKeyDown={props.onKeyDown}
      onKeyUp={props.onKeyUp}
      onTouchStart={props.onTouchStart}
      onTouchEnd={props.onTouchEnd}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      replace={replace}
      data-testid={dataTestId}
      role={role}
      aria-label={props['aria-label']}
      {...impressionAttributes}
      {...dataEngagementAttributes}
    >
      {children}
    </NavLink>
  );
};

export default ReactRouterLink;
